"use client";

/**
 * Client-Side Logger
 *
 * Environment-aware logging utility for client-side code.
 * Uses browser console for all environments with structured output.
 *
 * Provides the same API as console methods for easy replacement:
 * - clientLogger.log(...args)
 * - clientLogger.warn(...args)
 * - clientLogger.error(...args)
 * - clientLogger.info(...args)
 * - clientLogger.debug(...args)
 *
 * @example
 * ```typescript
 * import { clientLogger } from "./client-logger"
 *
 * // Replaces: console.log("User action", { userId })
 * clientLogger.log("User action", { userId })
 *
 * // Replaces: console.error("Failed to save", error)
 * clientLogger.error("Failed to save", error)
 * ```
 */

/**
 * Client logger that routes to browser console
 */
const createClientLogger = () => {
  const log = (level: "log" | "warn" | "error" | "info" | "debug", ...args: unknown[]): void => {
    switch (level) {
      case "log":
        console.log(...args);
        break;
      case "warn":
        console.warn(...args);
        break;
      case "error":
        console.error(...args);
        break;
      case "info":
        console.info(...args);
        break;
      case "debug":
        console.debug(...args);
        break;
    }
  };

  return {
    log: (...args: unknown[]) => log("log", ...args),
    warn: (...args: unknown[]) => log("warn", ...args),
    error: (...args: unknown[]) => log("error", ...args),
    info: (...args: unknown[]) => log("info", ...args),
    debug: (...args: unknown[]) => log("debug", ...args),
  };
};

/**
 * Client-side logger instance
 *
 * Use this in all client components, hooks, and client-side utilities
 * instead of console.log, console.error, etc.
 */
export const clientLogger = createClientLogger();
